/**
 * Reprendre la boutique là où le client l'a laissée après un RECHARGEMENT.
 *
 * Sur un téléphone modeste, Chrome vide l'onglet dès qu'on passe à une autre
 * application, puis le recharge au retour. Cas réel (Mme Gailterie, Android 10,
 * nuit du 07 au 08/10/2026) : une liste de courses dans une autre application,
 * un aller-retour toutes les 30 secondes, et à chaque retour la fiche ouverte
 * disparaissait et la liste repartait du haut — « le compte se clôture ».
 *
 * `sessionStorage` est le bon endroit : il survit au rechargement et à la purge
 * d'onglet par Chrome, il est propre à l'onglet, et il disparaît avec lui. Ce
 * n'est qu'un confort : tout accès est protégé (navigation privée, stockage
 * bloqué) et rien ne dépend de sa présence.
 */

const CLE_FICHE = 'boutique:fiche-ouverte';
const CLE_RECHERCHE = 'boutique:recherche';
const CLE_POSITION = 'boutique:position';

function lire(cle: string): string | null {
  try {
    return sessionStorage.getItem(cle);
  } catch {
    return null;
  }
}

function ecrire(cle: string, valeur: string | null): void {
  try {
    if (valeur === null || valeur === '') sessionStorage.removeItem(cle);
    else sessionStorage.setItem(cle, valeur);
  } catch {
    /* stockage indisponible : on perd seulement le confort */
  }
}

export const ficheOuverte = {
  lire: () => lire(CLE_FICHE),
  ecrire: (id: string | null) => ecrire(CLE_FICHE, id),
};

export const rechercheEnCours = {
  lire: () => lire(CLE_RECHERCHE) || '',
  ecrire: (texte: string) => ecrire(CLE_RECHERCHE, texte.trim() ? texte : null),
};

export interface PositionListe {
  /** La vue à laquelle la position appartient (rayon, recherche). */
  vue: string;
  /** Défilement de la zone produits, en pixels. */
  haut: number;
  /** Nombre de produits affichés (la liste se charge par tranches). */
  affiches: number;
}

export const positionListe = {
  lire: (): PositionListe | null => {
    const brut = lire(CLE_POSITION);
    if (!brut) return null;
    try {
      const p = JSON.parse(brut);
      return typeof p?.vue === 'string' && Number.isFinite(p?.haut) && Number.isFinite(p?.affiches) ? p : null;
    } catch {
      return null;
    }
  },
  ecrire: (p: PositionListe) => ecrire(CLE_POSITION, JSON.stringify(p)),
};

/** La « vue » courante : une position ne vaut que pour la même liste. */
export function vueCourante(search: string, recherche: string): string {
  return `${search}|${recherche.trim().toLowerCase()}`;
}
